import { useCallback } from 'react';
import { useBudgetDataCache } from '../../contexts/BudgetDataCacheContext';
import { createBudgetItem, updateBudgetItem, deleteBudgetItem, updateBudgetItemsBatch } from '../../api/budget';
import { BudgetItem } from '../../api/budget';

/**
 * BudgetItem 更新 Hook 配置选项
 */
export interface UseBudgetItemUpdatesOptions {
  onSuccess?: (action: 'create' | 'update' | 'delete', data: any) => void;
  onError?: (error: Error, action: 'create' | 'update' | 'delete', data: any) => void;
}

/**
 * LocalBudgetItem 类型（与 BudgetDataCacheContext 中的一致）
 */
interface LocalBudgetItem {
  budgetItemID: string;
  budgetItemCode: string;
  budgetItemName: string;
  budgetItemBelongBudgetID: string;
  budgetItemParentID: string | null;
  budgetItemAmount: number;
  budgetItemUsedAmount: number;
  budgetItemRemainingAmount: number;
  budgetItemProgressType: string;
  budgetItemCurrency?: string;
  budgetItemUnit1?: string | null;
  budgetItemUnit2?: string | null;
  budgetItemUnitPrice?: number;
  budgetItemQuantity1?: number;
  budgetItemQuantity2?: number;
  budgetItemRelatedID: string | null;
  budgetItemAssigneeID: string | null;
  budgetItemPartners: string[];
  budgetItemRemark: string;
  budgetItemFilmCode: string | null;
  budgetItemCreateTime: string;
  budgetItemUpdateTime: string;
  budgetItemLevel: number;
  budgetItemRole?: string;
  budgetItemOwnerID?: string;
  budgetItemCreatorID?: string;
  budgetItemUnusedAmount?: number;
  budgetItemExceedingAmount?: number;
  budgetItemStatus?: string;
  budgetItemDataStatus?: string;
  budgetItemChildren?: LocalBudgetItem[];
  budgetItemExpanded?: boolean;
  budgetItemChecked?: boolean;
  isRelatedItem?: boolean;
  isRelatedContainer?: boolean;
  relatedParentID?: string;
  calculatedRelatedAmount?: number;
}

/**
 * 在树形结构中查找项目
 */
const findItemInTree = (items: LocalBudgetItem[], itemId: string): LocalBudgetItem | null => {
  for (const item of items) {
    if (item.budgetItemID === itemId) {
      return item;
    }
    if (item.budgetItemChildren) {
      const found = findItemInTree(item.budgetItemChildren, itemId);
      if (found) return found;
    }
  }
  return null;
};

/**
 * 在树形结构中更新项目
 */
const updateItemInTree = (items: LocalBudgetItem[], itemId: string, updates: Partial<LocalBudgetItem>): LocalBudgetItem[] => {
  return items.map(item => {
    if (item.budgetItemID === itemId) {
      return { ...item, ...updates };
    }
    if (item.budgetItemChildren) {
      return {
        ...item,
        budgetItemChildren: updateItemInTree(item.budgetItemChildren, itemId, updates),
      };
    }
    return item;
  });
};

/**
 * 在树形结构中删除项目
 */
const removeItemFromTree = (items: LocalBudgetItem[], itemId: string): LocalBudgetItem[] => {
  return items
    .filter(item => item.budgetItemID !== itemId)
    .map(item => {
      if (item.budgetItemChildren) {
        return {
          ...item,
          budgetItemChildren: removeItemFromTree(item.budgetItemChildren, itemId),
        };
      }
      return item;
    });
};

/**
 * 在树形结构中添加项目
 */
const addItemToTree = (items: LocalBudgetItem[], newItem: LocalBudgetItem): LocalBudgetItem[] => {
  // 如果有父级，添加到父级的 children 中
  if (newItem.budgetItemParentID) {
    return items.map(item => {
      if (item.budgetItemID === newItem.budgetItemParentID) {
        return {
          ...item,
          budgetItemChildren: [...(item.budgetItemChildren || []), newItem],
        };
      }
      if (item.budgetItemChildren) {
        return {
          ...item,
          budgetItemChildren: addItemToTree(item.budgetItemChildren, newItem),
        };
      }
      return item;
    });
  }
  // 如果没有父级，添加到根级别
  return [...items, newItem];
};

/**
 * 将 BudgetItem 转换为 LocalBudgetItem
 */
const convertToLocalBudgetItem = (item: BudgetItem): LocalBudgetItem => {
  const amount = Number(item.budgetItemAmount || 0);
  const usedAmount = Number(item.budgetItemUsedAmount || 0);
  
  return {
    ...item,
    budgetItemAmount: amount,
    budgetItemUsedAmount: usedAmount,
    budgetItemRemainingAmount: amount - usedAmount,
    budgetItemCurrency: item.budgetItemCurrency || 'RMB',
    budgetItemRelatedID: item.budgetItemRelatedID || null,
    budgetItemAssigneeID: item.budgetItemAssigneeID || null,
    budgetItemPartners: [],
    budgetItemRemark: item.budgetItemRemark || '',
    budgetItemCreateTime: item.budgetItemCreateTime || new Date().toISOString(),
    budgetItemUpdateTime: item.budgetItemUpdateTime || new Date().toISOString(),
    budgetItemChildren: [],
    budgetItemExpanded: true,
    budgetItemChecked: false,
    budgetItemLevel: item.budgetItemLevel || 0,
  };
};

/**
 * BudgetItem 更新 Hook
 * 提供统一的 BudgetItem 数据更新方法，包括：
 * - 创建预算项
 * - 更新预算项
 * - 删除预算项
 * - 批量更新预算项
 * 
 * 更新流程：
 * 1. 乐观更新缓存
 * 2. 通知本标签页订阅者（UI 立即更新）
 * 3. 调用 API
 * 4. 成功：通过 BroadcastChannel 通知其他标签页
 * 5. 失败：回滚缓存，通知订阅者
 */
export function useBudgetItemUpdates(options?: UseBudgetItemUpdatesOptions) {
  const { getCache, setCache } = useBudgetDataCache();

  /**
   * 创建预算项
   */
  const createBudgetItemData = useCallback(
    async (budgetId: string, data: Partial<BudgetItem>): Promise<BudgetItem> => {
      let oldCache: any = null;
      let oldItems: LocalBudgetItem[] = [];
      let tempItemId: string | null = null;

      try {
        // 乐观更新缓存
        const cache = getCache(budgetId);
        if (cache) {
          oldCache = { ...cache };
          oldItems = JSON.parse(JSON.stringify(cache.budgetItems)); // 深拷贝

          // 创建临时项目（使用传入的 budgetItemID 或生成新的）
          tempItemId = data.budgetItemID || `temp_${Date.now()}`;
          const tempItem: LocalBudgetItem = convertToLocalBudgetItem({
            ...data,
            budgetItemID: tempItemId,
            budgetItemBelongBudgetID: budgetId,
          } as BudgetItem);

          // 添加到缓存
          const updatedItems = addItemToTree(cache.budgetItems, tempItem);
          setCache(budgetId, {
            budgetItems: updatedItems,
          });
        }

        // 调用 API
        const response = await createBudgetItem(data);
        const newItem = response.data;

        if (!newItem) {
          throw new Error('创建预算项失败：未返回数据');
        }

        // 更新缓存为新创建的数据
        if (cache && tempItemId) {
          // 先移除临时项目
          const itemsWithoutTemp = removeItemFromTree(oldItems, tempItemId);
          // 添加新创建的项目
          const finalItems = addItemToTree(itemsWithoutTemp, convertToLocalBudgetItem(newItem));
          setCache(budgetId, {
            budgetItems: finalItems,
          });
        }

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('create', { budgetId, item: newItem });
        }

        return newItem;
      } catch (error) {
        console.error('[useBudgetItemUpdates] 创建预算项失败:', error);

        // 回滚缓存
        if (budgetId && oldCache) {
          setCache(budgetId, {
            budgetItems: oldItems,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'create', { budgetId, data });
        }

        throw error;
      }
    },
    [getCache, setCache, options]
  );

  /**
   * 更新预算项
   */
  const updateBudgetItemData = useCallback(
    async (budgetId: string, itemId: string, updates: Partial<BudgetItem>) => {
      let oldCache: any = null;
      let oldItems: LocalBudgetItem[] = [];

      try {
        // 乐观更新缓存
        const cache = getCache(budgetId);
        if (cache) {
          oldCache = { ...cache };
          oldItems = JSON.parse(JSON.stringify(cache.budgetItems)); // 深拷贝

          // 查找要更新的项目
          const itemToUpdate = findItemInTree(cache.budgetItems, itemId);
          if (itemToUpdate) {
            // 合并更新
            const updatedItem = {
              ...itemToUpdate,
              ...updates,
            } as LocalBudgetItem;

            // 更新树中的项目
            const updatedItems = updateItemInTree(cache.budgetItems, itemId, updatedItem);
            setCache(budgetId, {
              budgetItems: updatedItems,
            });
          }
        }

        // 调用 API
        await updateBudgetItem(itemId, updates);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('update', { budgetId, itemId, updates });
        }
      } catch (error) {
        console.error('[useBudgetItemUpdates] 更新预算项失败:', error);

        // 回滚缓存
        if (budgetId && oldCache) {
          setCache(budgetId, {
            budgetItems: oldItems,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'update', { budgetId, itemId, updates });
        }

        throw error;
      }
    },
    [getCache, setCache, options]
  );

  /**
   * 删除预算项
   */
  const deleteBudgetItemData = useCallback(
    async (budgetId: string, itemId: string) => {
      let oldCache: any = null;
      let oldItems: LocalBudgetItem[] = [];

      try {
        // 乐观更新缓存
        const cache = getCache(budgetId);
        if (cache) {
          oldCache = { ...cache };
          oldItems = JSON.parse(JSON.stringify(cache.budgetItems)); // 深拷贝

          // 从缓存中移除
          const updatedItems = removeItemFromTree(cache.budgetItems, itemId);
          setCache(budgetId, {
            budgetItems: updatedItems,
          });
        }

        // 调用 API
        await deleteBudgetItem(itemId);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('delete', { budgetId, itemId });
        }
      } catch (error) {
        console.error('[useBudgetItemUpdates] 删除预算项失败:', error);

        // 回滚缓存
        if (budgetId && oldCache) {
          setCache(budgetId, {
            budgetItems: oldItems,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'delete', { budgetId, itemId });
        }

        throw error;
      }
    },
    [getCache, setCache, options]
  );

  /**
   * 批量更新预算项
   */
  const updateBudgetItemsData = useCallback(
    async (budgetId: string, updates: Array<{ budgetItemID: string; data: Partial<BudgetItem> }>) => {
      let oldCache: any = null;
      let oldItems: LocalBudgetItem[] = [];

      try {
        // 乐观更新缓存
        const cache = getCache(budgetId);
        if (cache) {
          oldCache = { ...cache };
          oldItems = JSON.parse(JSON.stringify(cache.budgetItems)); // 深拷贝

          // 批量更新树中的项目
          let updatedItems = cache.budgetItems;
          for (const update of updates) {
            const itemToUpdate = findItemInTree(updatedItems, update.budgetItemID);
            if (itemToUpdate) {
              const updatedItem = {
                ...itemToUpdate,
                ...update.data,
              } as LocalBudgetItem;
              updatedItems = updateItemInTree(updatedItems, update.budgetItemID, updatedItem);
            }
          }

          setCache(budgetId, {
            budgetItems: updatedItems,
          });
        }

        // 调用批量更新 API
        await updateBudgetItemsBatch(updates);

        // 成功回调
        updates.forEach(update => {
          if (options?.onSuccess) {
            options.onSuccess('update', { budgetId, itemId: update.budgetItemID, updates: update.data });
          }
        });
      } catch (error) {
        console.error('[useBudgetItemUpdates] 批量更新预算项失败:', error);

        // 回滚缓存
        if (budgetId && oldCache) {
          setCache(budgetId, {
            budgetItems: oldItems,
          });
        }

        // 错误回调
        updates.forEach(update => {
          if (options?.onError) {
            options.onError(error as Error, 'update', { budgetId, itemId: update.budgetItemID, updates: update.data });
          }
        });

        throw error;
      }
    },
    [getCache, setCache, options]
  );

  return {
    createBudgetItemData,
    updateBudgetItemData,
    deleteBudgetItemData,
    updateBudgetItemsData,
  };
}
